<script setup lang="ts">
import { Ref, ref, onMounted, onBeforeUnmount } from 'vue'
import i18n from '@/lang/i18n'

const emit = defineEmits<{
  (e: 'update:height', height: number): void
}>()

const VIEWPORT_BOTTOM_SPACE = 120

type Props = {
  getHeight: () => number;
  height?: number | null;
  minHeight?: number;
  maxHeight?: number | null;
  keyStep?: number;
};

const props = withDefaults(defineProps<Props>(), {
  height: null,
  minHeight: 200,
  maxHeight: null,
  keyStep: 40,
})

const startY: Ref<number> = ref(0)
const startHeight: Ref<number> = ref(0)

const currentHeight = (): number => props.height || props.getHeight()

const clampHeight = (height: number): number => {
  const max = Math.max(props.minHeight, props.maxHeight || window.innerHeight - VIEWPORT_BOTTOM_SPACE)
  return Math.min(Math.max(Math.round(height), props.minHeight), max)
}

const resize = (event: PointerEvent) => {
  emit('update:height', clampHeight(startHeight.value + event.clientY - startY.value))
}

const removeResizeListeners = () => {
  document.removeEventListener('pointermove', resize)
  document.removeEventListener('pointerup', endResize)
  document.removeEventListener('pointercancel', endResize)
  document.body.classList.remove('resize-handle-dragging')
}

function endResize () {
  removeResizeListeners()
}

const startResize = (event: PointerEvent) => {
  const height = currentHeight()
  if (!height) {
    return
  }
  event.preventDefault()
  startY.value = event.clientY
  startHeight.value = height
  const handle = event.currentTarget as HTMLElement
  handle.setPointerCapture?.(event.pointerId)
  document.addEventListener('pointermove', resize)
  document.addEventListener('pointerup', endResize)
  document.addEventListener('pointercancel', endResize)
  document.body.classList.add('resize-handle-dragging')
}

const resizeByKey = (step: number) => {
  const height = currentHeight()
  if (!height) return
  emit('update:height', clampHeight(height + step))
}

const fitToViewport = () => {
  const height = currentHeight()
  if (!height) {
    return
  }
  const clamped = clampHeight(height)
  if (clamped < height) {
    emit('update:height', clamped)
  }
}

onMounted(() => {
  window.addEventListener('resize', fitToViewport)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', fitToViewport)
  removeResizeListeners()
})
</script>

<template>
  <div
    class="resize-handle"
    role="separator"
    aria-orientation="horizontal"
    tabindex="0"
    :title="i18n.global.t('pages.common.resizeHandle')"
    @pointerdown="startResize"
    @keydown.down.prevent="resizeByKey(props.keyStep)"
    @keydown.up.prevent="resizeByKey(-props.keyStep)"
  >
    <span class="resize-handle__grip"></span>
  </div>
</template>

<style>
body.resize-handle-dragging {
  cursor: ns-resize;
  user-select: none;
}
</style>
<style scoped>
.resize-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 12px;
  cursor: ns-resize;
  touch-action: none;
  background-color: #f8f9fa;
  border-top: 1px solid #dee2e6;
}
.resize-handle:hover .resize-handle__grip,
.resize-handle:focus-visible .resize-handle__grip {
  background-color: #6c757d;
}
.resize-handle__grip {
  width: 40px;
  height: 3px;
  border-radius: 2px;
  background-color: #adb5bd;
}
</style>
